import React, { useState } from 'react';
import { FaRocket, FaBuilding, FaUsers, FaChalkboardTeacher, FaUserGraduate, FaCheck, FaTimes } from 'react-icons/fa';
import { organizationService } from '../../services/organizationService';
import { databaseService, OrganizationData } from '../../services/databaseService';

interface QuickOrganizationSetupProps {
  onOrganizationCreated: (organization: OrganizationData) => void;
  onCancel: () => void;
}

interface QuickSetupData {
  organizationName: string;
  adminEmail: string;
  adminName: string;
  adminLastName: string;
  template: 'small' | 'medium' | 'large';
  includeStudents: boolean;
}

export const QuickOrganizationSetup: React.FC<QuickOrganizationSetupProps> = ({
  onOrganizationCreated,
  onCancel
}) => {
  const [formData, setFormData] = useState<QuickSetupData>({
    organizationName: '',
    adminEmail: '',
    adminName: '',
    adminLastName: '',
    template: 'medium',
    includeStudents: false
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const templates = {
    small: {
      name: 'Organización Pequeña',
      description: '3 cursos, 4 profesores',
      courses: [
        { name: '1° Básico A', level: 'Básico' },
        { name: '2° Básico A', level: 'Básico' },
        { name: '3° Básico A', level: 'Básico' }
      ],
      teachers: [
        { firstName: 'María', lastName: 'González', courses: ['1° Básico A'] },
        { firstName: 'Juan', lastName: 'Pérez', courses: ['2° Básico A'] },
        { firstName: 'Ana', lastName: 'López', courses: ['3° Básico A'] },
        { firstName: 'Carlos', lastName: 'Martínez', courses: ['1° Básico A', '2° Básico A'] }
      ],
      studentsPerCourse: 15
    },
    medium: {
      name: 'Organización Mediana',
      description: '6 cursos, 8 profesores',
      courses: [
        { name: '1° Básico A', level: 'Básico' },
        { name: '2° Básico A', level: 'Básico' },
        { name: '3° Básico A', level: 'Básico' },
        { name: '4° Básico A', level: 'Básico' },
        { name: '5° Básico A', level: 'Básico' },
        { name: '6° Básico A', level: 'Básico' }
      ],
      teachers: [
        { firstName: 'María', lastName: 'González', courses: ['1° Básico A'] },
        { firstName: 'Juan', lastName: 'Pérez', courses: ['2° Básico A'] },
        { firstName: 'Ana', lastName: 'López', courses: ['3° Básico A'] },
        { firstName: 'Carlos', lastName: 'Martínez', courses: ['4° Básico A'] },
        { firstName: 'Patricia', lastName: 'Silva', courses: ['5° Básico A'] },
        { firstName: 'Roberto', lastName: 'Morales', courses: ['6° Básico A'] },
        { firstName: 'Carmen', lastName: 'Torres', courses: ['1° Básico A', '2° Básico A'] },
        { firstName: 'Miguel', lastName: 'Vargas', courses: ['3° Básico A', '4° Básico A'] }
      ],
      studentsPerCourse: 25
    },
    large: {
      name: 'Organización Grande',
      description: '12 cursos, 15 profesores',
      courses: [
        { name: '1° Básico A', level: 'Básico' },
        { name: '2° Básico A', level: 'Básico' },
        { name: '3° Básico A', level: 'Básico' },
        { name: '4° Básico A', level: 'Básico' },
        { name: '5° Básico A', level: 'Básico' },
        { name: '6° Básico A', level: 'Básico' },
        { name: '7° Básico A', level: 'Básico' },
        { name: '8° Básico A', level: 'Básico' },
        { name: '1° Medio A', level: 'Medio' },
        { name: '2° Medio A', level: 'Medio' },
        { name: '3° Medio A', level: 'Medio' },
        { name: '4° Medio A', level: 'Medio' }
      ],
      teachers: [
        { firstName: 'María', lastName: 'González', courses: ['1° Básico A', '2° Básico A'] },
        { firstName: 'Juan', lastName: 'Pérez', courses: ['3° Básico A', '4° Básico A'] },
        { firstName: 'Ana', lastName: 'López', courses: ['5° Básico A', '6° Básico A'] },
        { firstName: 'Carlos', lastName: 'Martínez', courses: ['7° Básico A', '8° Básico A'] },
        { firstName: 'Patricia', lastName: 'Silva', courses: ['1° Medio A', '2° Medio A'] },
        { firstName: 'Roberto', lastName: 'Morales', courses: ['3° Medio A', '4° Medio A'] },
        { firstName: 'Carmen', lastName: 'Torres', courses: ['1° Básico A'] },
        { firstName: 'Miguel', lastName: 'Vargas', courses: ['2° Básico A'] },
        { firstName: 'Sandra', lastName: 'Rojas', courses: ['3° Básico A'] },
        { firstName: 'Luis', lastName: 'Ramírez', courses: ['4° Básico A'] },
        { firstName: 'Elena', lastName: 'Castro', courses: ['5° Básico A'] },
        { firstName: 'Diego', lastName: 'Herrera', courses: ['6° Básico A'] },
        { firstName: 'Mónica', lastName: 'Vega', courses: ['7° Básico A'] },
        { firstName: 'Andrés', lastName: 'Soto', courses: ['8° Básico A'] },
        { firstName: 'Claudia', lastName: 'Mendoza', courses: ['1° Medio A', '2° Medio A'] }
      ],
      studentsPerCourse: 30
    }
  };

  const generateSecurePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  const generateTenantKey = (displayName: string) => {
    return displayName
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const generateStudents = (courseName: string, count: number) => {
    const firstNames = ['Alejandro', 'Sofía', 'Mateo', 'Valentina', 'Diego', 'Isabella', 'Santiago', 'Camila', 'Sebastián', 'Martina', 'Nicolás', 'Antonia', 'Benjamín', 'Emilia', 'Joaquín', 'Florencia', 'Tomás', 'Amanda', 'Agustín', 'Catalina'];
    const lastNames = ['González', 'Rodríguez', 'Pérez', 'López', 'Martínez', 'García', 'Hernández', 'Morales', 'Silva', 'Torres', 'Vargas', 'Castro', 'Rojas', 'Herrera', 'Mendoza', 'Soto', 'Vega', 'Ramírez', 'Flores', 'Muñoz'];
    
    const students = [];
    for (let i = 0; i < count; i++) {
      const firstName = firstNames[Math.floor(Math.random() * firstNames.length)];
      const lastName = lastNames[Math.floor(Math.random() * lastNames.length)];
      const id = `${Math.floor(Math.random() * 90000000) + 10000000}`;
      
      students.push({
        id,
        name: `${firstName} ${lastName}`,
        firstName,
        lastName,
        course: courseName,
        gender: Math.random() > 0.5 ? 'M' : 'F' as 'M' | 'F',
        birthDate: `200${Math.floor(Math.random() * 10)}-${String(Math.floor(Math.random() * 12) + 1).padStart(2, '0')}-${String(Math.floor(Math.random() * 28) + 1).padStart(2, '0')}`,
        email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@estudiante.cl`
      });
    }
    return students;
  };

  const handleQuickSetup = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!formData.organizationName || !formData.adminEmail || !formData.adminName || !formData.adminLastName) {
        setError('Por favor completa todos los campos obligatorios');
        return;
      }

      const tenantKey = generateTenantKey(formData.organizationName);
      const domain = `${tenantKey}.cl`;
      const template = templates[formData.template];

      // Crear la organización
      const createOrgData = {
        displayName: formData.organizationName,
        tenantKey,
        domains: [domain],
        status: 'trial' as const,
        branding: {
          logoUrl: '',
          faviconUrl: '',
          colors: {
            primary: '#3B82F6',
            secondary: '#10B981',
            accent: '#F59E0B'
          }
        },
        settings: {
          maxUsers: 100,
          maxStudents: 1000,
          features: ['basic_evaluation', 'reports', 'user_management', 'data_import']
        },
        adminUser: {
          username: 'admin',
          email: formData.adminEmail,
          firstName: formData.adminName,
          lastName: formData.adminLastName,
          password: generateSecurePassword()
        }
      };

      const result = await organizationService.createOrganization(createOrgData);

      if (!result.success) {
        throw new Error(result.message || 'Error al crear la organización');
      }

      const organization = result.organization!;

      // Crear profesores
      for (const teacherData of template.teachers) {
        await databaseService.createTeacher(organization._id, {
          username: `${teacherData.firstName.toLowerCase()}.${teacherData.lastName.toLowerCase()}`,
          email: `${teacherData.firstName.toLowerCase()}.${teacherData.lastName.toLowerCase()}@${domain}`,
          firstName: teacherData.firstName,
          lastName: teacherData.lastName,
          role: 'teacher',
          courses: teacherData.courses,
          phone: '',
          isActive: true,
          mustChangePassword: true,
          password: generateSecurePassword()
        });
      }

      // Crear cursos
      for (const courseData of template.courses) {
        await databaseService.createCourse(organization._id, {
          name: courseData.name,
          level: courseData.level,
          teacherIds: []
        });
      }

      // Crear estudiantes si está habilitado
      if (formData.includeStudents) {
        for (const courseData of template.courses) {
          const students = generateStudents(courseData.name, template.studentsPerCourse);
          for (const studentData of students) {
            await databaseService.createStudent(organization._id, studentData);
          }
        }
      }

      onOrganizationCreated(organization);

    } catch (error) {
      console.error('Error creating quick organization:', error);
      setError(error instanceof Error ? error.message : 'Error al crear la organización');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-purple-600 px-6 py-4 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <FaRocket className="mr-3" size={24} />
              <h2 className="text-xl font-semibold">Configuración Rápida</h2>
            </div>
            <button
              onClick={onCancel}
              className="text-white hover:text-gray-200"
            >
              <FaTimes size={20} />
            </button>
          </div>
          <p className="mt-2 text-blue-100">Crea una organización completa en menos de 2 minutos</p>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[70vh]">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          <div className="space-y-6">
            {/* Información Básica */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <FaBuilding className="mr-2 text-blue-500" />
                Información Básica
              </h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre de la Organización *
                </label>
                <input
                  type="text"
                  value={formData.organizationName}
                  onChange={(e) => setFormData(prev => ({ ...prev, organizationName: e.target.value }))}
                  placeholder="Ej: Colegio San José"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre del Administrador *
                  </label>
                  <input
                    type="text"
                    value={formData.adminName}
                    onChange={(e) => setFormData(prev => ({ ...prev, adminName: e.target.value }))}
                    placeholder="Nombre"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Apellido del Administrador *
                  </label>
                  <input
                    type="text"
                    value={formData.adminLastName}
                    onChange={(e) => setFormData(prev => ({ ...prev, adminLastName: e.target.value }))}
                    placeholder="Apellido"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email del Administrador *
                </label>
                <input
                  type="email"
                  value={formData.adminEmail}
                  onChange={(e) => setFormData(prev => ({ ...prev, adminEmail: e.target.value }))}
                  placeholder="admin@ejemplo.cl"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Selección de Plantilla */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <FaUsers className="mr-2 text-green-500" />
                Tamaño de la Organización
              </h3>
              
              <div className="grid grid-cols-1 gap-3">
                {Object.entries(templates).map(([key, template]) => (
                  <div
                    key={key}
                    onClick={() => setFormData(prev => ({ ...prev, template: key as 'small' | 'medium' | 'large' }))}
                    className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                      formData.template === key
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold text-gray-900">{template.name}</h4>
                        <p className="text-sm text-gray-600">{template.description}</p>
                      </div>
                      {formData.template === key && (
                        <FaCheck className="text-blue-600" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Opciones Adicionales */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <FaUserGraduate className="mr-2 text-purple-500" />
                Opciones Adicionales
              </h3>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="includeStudents"
                  checked={formData.includeStudents}
                  onChange={(e) => setFormData(prev => ({ ...prev, includeStudents: e.target.checked }))}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="includeStudents" className="ml-2 text-sm text-gray-700">
                  Incluir estudiantes de muestra ({templates[formData.template].studentsPerCourse} por curso)
                </label>
              </div>
            </div>

            {/* Resumen */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-2">Resumen de la Configuración</h4>
              <div className="text-sm text-gray-600 space-y-1">
                <div>• {templates[formData.template].courses.length} cursos</div>
                <div>• {templates[formData.template].teachers.length} profesores</div>
                {formData.includeStudents && (
                  <div>• {templates[formData.template].courses.length * templates[formData.template].studentsPerCourse} estudiantes</div>
                )}
                <div>• Dominio: {formData.organizationName ? generateTenantKey(formData.organizationName) + '.cl' : 'ejemplo.cl'}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            Cancelar
          </button>

          <button
            onClick={handleQuickSetup}
            disabled={loading || !formData.organizationName || !formData.adminEmail || !formData.adminName || !formData.adminLastName}
            className="flex items-center px-6 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded hover:from-blue-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Creando Organización...
              </>
            ) : (
              <>
                <FaRocket className="mr-2" />
                Crear Organización Rápida
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};